import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getTemplate, createTemplate, updateTemplate, assignMembersToTemplate, deleteTemplate, getTemplates } from 'team/apis/teamTemplateApi';
import { getCardById } from 'apis/cardApi';
import { getContactLinksByCard } from 'apis/contactLinkApi';
import { TeamTemplateProvider, useTeamTemplateContext } from 'team/contexts/TeamTemplateContext';
import TeamTemplateEditNavbar from 'team/components/templates/TeamTemplateEditNavbar';
import TeamTemplateEditLayout from 'team/components/templates/TeamTemplateEditLayout';
import TeamTemplateEditContent from 'team/components/templates/TeamTemplateEditContent';
import TeamTemplatePreview from 'team/components/templates/TeamTemplatePreview';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import TeamTemplateAssignMembersModal from 'team/components/templates/TeamTemplateAssignMembersModal';

const TeamTemplateEditContainerInner = ({ user, team }) => {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const teamId = team?._id || team?.id;
    const { templateState, updateTemplateState, setTemplateSection, loadTemplateFromServer } = useTeamTemplateContext();

    // Check if creating - handle both /team/template/create and /template/create
    const isCreating = !templateId || location.pathname.includes('/create');
    // Note: createType removed - "from-scratch" vs "from-card" can be handled via modal/state if needed in future

    // Get templates count from location state to calculate next template number
    const templatesCount = location.state?.templatesCount || 0;
    const nextTemplateNumber = templatesCount + 1;
    const defaultTemplateName = `Template ${nextTemplateNumber}`;

    const [template, setTemplate] = useState({
        templateName: defaultTemplateName,
        about: {},
        links: {},
        qrCode: {},
        leadCapture: {},
        followUpEmail: {},
        settings: {
            allowNonAdminsToAssign: false
        }
    });
    const [selectedItem, setSelectedItem] = useState('about');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [assignModalOpen, setAssignModalOpen] = useState(false);

    // Use a consistent template ID for context
    const currentTemplateId = templateId || 'new-template';
    const cardDataLoadedRef = useRef(false);
    const templateLoadedRef = useRef(null); // Store the template ID that was loaded, not just true/false

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Helper function to map card data to template structure
    const mapCardToTemplate = async (card) => {
        try {
            // Fetch full card data if only card ID is provided
            let fullCardData = card;
            if (card._id && !card.contact_info) {
                const cardResponse = await getCardById(card._id);
                if (cardResponse && cardResponse.success && cardResponse.card) {
                    fullCardData = cardResponse.card;
                }
            }

            // Fetch contact links for the card
            let contactLinks = [];
            let customLinkTypeLabels = {};
            if (fullCardData._id) {
                try {
                    const linksResponse = await getContactLinksByCard(fullCardData._id);
                    if (linksResponse && linksResponse.success && Array.isArray(linksResponse.contactLinks)) {
                        contactLinks = linksResponse.contactLinks.map(link => link._id || link);
                    }
                } catch (error) {
                    console.warn('Error fetching contact links:', error);
                }
            }

            // Get custom link type labels from card
            if (fullCardData.customLinkTypeLabels) {
                if (fullCardData.customLinkTypeLabels instanceof Map) {
                    customLinkTypeLabels = Object.fromEntries(fullCardData.customLinkTypeLabels);
                } else {
                    customLinkTypeLabels = fullCardData.customLinkTypeLabels;
                }
            }

            // Extract card full name - prioritize contact_info.fullName (the card owner's name)
            // This is what should be used as the template name
            const cardFullName = fullCardData.contact_info?.fullName ||
                fullCardData.cardName ||
                fullCardData.fullName ||
                'Untitled Card';

            // Map lead capture input fields from card to template format
            // Copy all field properties including type and config (for dropdowns, etc.)
            const leadCaptureFields = (fullCardData.leadCaptureInputFields || []).map((field, index) => {
                // Generate a name from the label (lowercase, replace spaces with underscores)
                const fieldName = field.label
                    ? field.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                    : `field_${index}`;

                return {
                    name: fieldName,
                    label: field.label || '',
                    required: field.required || false,
                    enabled: true,
                    // Copy type and config to preserve dropdown options and other field configurations
                    type: field.type || 'text',
                    config: field.config || {}
                };
            });

            // Map card data to template structure
            const mappedTemplate = {
                templateName: cardFullName, // Use card's full name as template name
                about: {
                    cardName: cardFullName,
                    bio: fullCardData.bio || '',
                    cardLayout: fullCardData.cardLayout || 'left-aligned',
                    cardTheme: fullCardData.cardTheme || '#4A90E2',
                    linkColor: fullCardData.linkColor || fullCardData.cardTheme || '#4A90E2',
                    font: fullCardData.font || 'Arial',
                    matchLinkIconsToTheme: fullCardData.matchLinkIconsToTheme || false,
                    // Copy all image URLs
                    profilePictureUrl: fullCardData.profilePictureUrl || '',
                    coverPhotoUrl: fullCardData.coverPhotoUrl || '',
                    companyLogoUrl: fullCardData.companyLogoUrl || '',
                    location: fullCardData.contact_info?.location || fullCardData.location || ''
                },
                links: {
                    contactLinks: contactLinks,
                    customLinkTypeLabels: customLinkTypeLabels
                },
                qrCode: {
                    color: fullCardData.cardTheme || '#000000',
                    logoUrl: fullCardData.companyLogoUrl || null,
                    size: 200,
                    errorCorrectionLevel: 'H'
                },
                leadCapture: {
                    enabled: fullCardData.leadCaptureModeOn || false,
                    // Use the card's form header as title, or default
                    title: fullCardData.leadCaptureFormHeader || 'Get in Touch',
                    description: 'Fill out this form and I\'ll get back to you soon!',
                    // Copy all lead capture input fields from the card
                    fields: leadCaptureFields,
                    submitButtonText: 'Submit',
                    successMessage: 'Thanks! I\'ll be in touch soon.'
                },
                followUpEmail: {
                    autoFollowUpIntroEmail: fullCardData.autoFollowUpIntroEmail !== undefined
                        ? fullCardData.autoFollowUpIntroEmail
                        : true,
                    subject: 'New Connection - {leadName} connected with {userName}',
                    message: '',
                    delayHours: 0,
                    delayMinutes: 0,
                    toEmails: {
                        newLeadsEmail: '',
                        userEmail: ''
                    },
                    ccRecipients: '',
                    bccRecipients: ''
                }
            };

            return mappedTemplate;
        } catch (error) {
            console.error('Error mapping card to template:', error);
            throw error;
        }
    };

    // Initialize template state in context when template changes
    useEffect(() => {
        if (template) {
            updateTemplateState(currentTemplateId, template);
        }
    }, [template, currentTemplateId, updateTemplateState]);

    // Load template from card if creating from card
    useEffect(() => {
        const loadTemplateFromCard = async () => {
            if (!isCreating) return;
            if (cardDataLoadedRef.current) return;

            const fromCard = location.state?.fromCard;
            if (!fromCard) {
                // No card provided, initialize empty template with default name
                const defaultName = `Template ${nextTemplateNumber}`;
                setIsLoading(false);
                setTemplate({
                    templateName: defaultName,
                    about: {},
                    links: {},
                    qrCode: {},
                    leadCapture: {},
                    followUpEmail: {},
                    settings: {
                        allowNonAdminsToAssign: false
                    }
                });
                updateTemplateState(currentTemplateId, {
                    templateName: defaultName,
                    about: {},
                    links: {},
                    qrCode: {},
                    leadCapture: {},
                    followUpEmail: {},
                    settings: {
                        allowNonAdminsToAssign: false
                    }
                });
                return;
            }

            // Card provided, map it to template
            try {
                setIsLoading(true);
                cardDataLoadedRef.current = true;

                const mappedTemplate = await mapCardToTemplate(fromCard);
                setTemplate(mappedTemplate);
                updateTemplateState(currentTemplateId, mappedTemplate);
            } catch (error) {
                console.error('Error loading template from card:', error);
                showSnackbar(error.message || 'Failed to load card data', 'error');
                // Fallback to empty template with default name
                const defaultName = `Template ${nextTemplateNumber}`;
                setTemplate({
                    templateName: defaultName,
                    about: {},
                    links: {},
                    qrCode: {},
                    leadCapture: {},
                    followUpEmail: {},
                    settings: {
                        allowNonAdminsToAssign: false
                    }
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadTemplateFromCard();
    }, [isCreating, location.state, currentTemplateId, updateTemplateState]);

    // Load template if editing (using context's loadTemplateFromServer)
    useEffect(() => {
        const loadTemplate = async () => {
            if (isCreating) {
                // Creating is handled by loadTemplateFromCard
                return;
            }

            if (!teamId || !templateId || !loadTemplateFromServer) return;

            // Prevent multiple loads for the same template in the same session
            // But allow reload when navigating back (templateLoadedRef will be reset on unmount)
            if (templateLoadedRef.current === templateId) {
                console.log('[TeamTemplateEditContainer] Template already loaded, skipping reload');
                return;
            }

            try {
                setIsLoading(true);
                // Use context's loadTemplateFromServer (similar to card update pattern)
                const loadedTemplate = await loadTemplateFromServer(teamId, templateId);

                if (loadedTemplate) {
                    // Ensure settings object exists with default values
                    if (!loadedTemplate.settings) {
                        loadedTemplate.settings = {
                            allowNonAdminsToAssign: false
                        };
                    }

                    // Log image URLs when template loads to verify they're in the database
                    console.log('[TeamTemplateEditContainer] Template loaded from database:', {
                        templateId: loadedTemplate._id,
                        templateName: loadedTemplate.templateName,
                        profilePictureUrl: loadedTemplate.about?.profilePictureUrl,
                        coverPhotoUrl: loadedTemplate.about?.coverPhotoUrl,
                        companyLogoUrl: loadedTemplate.about?.companyLogoUrl
                    });

                    // ⭐ CRITICAL FIX: Use loadedTemplate directly (fresh from database)
                    // Don't rely on context which might be stale or empty when navigating back
                    // This ensures image URLs from Firebase Storage are properly loaded
                    setTemplate(prev => {
                        // Only update if different to prevent unnecessary re-renders
                        if (prev._id === loadedTemplate._id) {
                            const prevStr = JSON.stringify(prev);
                            const loadedStr = JSON.stringify(loadedTemplate);
                            if (prevStr === loadedStr) {
                                return prev; // No change, return previous to prevent re-render
                            }
                        }
                        return loadedTemplate;
                    });

                    // Update context with the fresh template data from database
                    // Only update if context doesn't already have the same data to prevent infinite loops
                    const contextTemplate = templateState[templateId];
                    const contextStr = contextTemplate ? JSON.stringify(contextTemplate) : '';
                    const loadedStr = JSON.stringify(loadedTemplate);
                    if (contextStr !== loadedStr) {
                        updateTemplateState(currentTemplateId, loadedTemplate);
                    }

                    // Mark as loaded
                    templateLoadedRef.current = templateId;
                }
            } catch (error) {
                console.error('Error loading template:', error);
                showSnackbar(error.message || 'Failed to load template', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        loadTemplate();

        // Don't reset ref on unmount - keep it to prevent unnecessary reloads
        // The ref will be checked at the start of the effect to prevent duplicate loads
    }, [templateId, teamId, isCreating, currentTemplateId, loadTemplateFromServer]); // CRITICAL: Removed templateState from dependencies to prevent infinite loops

    const handleTemplateUpdate = (section, data) => {
        if (section === 'templateName') {
            // Handle template name update at root level
            const updatedTemplate = {
                ...template,
                templateName: data
            };
            setTemplate(updatedTemplate);
            // Update context
            updateTemplateState(currentTemplateId, updatedTemplate);
        } else if (section === 'settings') {
            // Handle settings update - merge with existing settings
            const updatedTemplate = {
                ...template,
                settings: {
                    ...(template.settings || {}),
                    ...data
                }
            };
            setTemplate(updatedTemplate);
            // Update context
            setTemplateSection(currentTemplateId, section, data);
            updateTemplateState(currentTemplateId, updatedTemplate);
        } else {
            // Handle section updates (especially 'about' section with image URLs)
            // CRITICAL: For 'about' section, ensure we do a deep merge to preserve all properties including image URLs
            let updatedTemplate = {
                ...template,
                [section]: section === 'about'
                    ? { ...(template[section] || {}), ...data } // Deep merge for about section
                    : { ...template[section], ...data }
            };

            // CRITICAL: For 'about' section, explicitly ensure image URLs from data are preserved
            // This is essential because data contains the freshly uploaded Firebase URLs
            if (section === 'about' && data) {
                updatedTemplate.about = {
                    ...updatedTemplate.about,
                    // Explicitly set image URLs from data if they exist (they're the Firebase URLs)
                    // This ensures they override any null/blob values that might be in the existing about
                    profilePictureUrl: data.profilePictureUrl !== undefined ? data.profilePictureUrl : updatedTemplate.about?.profilePictureUrl,
                    coverPhotoUrl: data.coverPhotoUrl !== undefined ? data.coverPhotoUrl : updatedTemplate.about?.coverPhotoUrl,
                    companyLogoUrl: data.companyLogoUrl !== undefined ? data.companyLogoUrl : updatedTemplate.about?.companyLogoUrl
                };

                console.log('[TeamTemplateEditContainer] ✅ About section updated - Image URLs:', {
                    fromData: {
                        profilePictureUrl: data.profilePictureUrl,
                        coverPhotoUrl: data.coverPhotoUrl,
                        companyLogoUrl: data.companyLogoUrl
                    },
                    inUpdatedTemplate: {
                        profilePictureUrl: updatedTemplate.about.profilePictureUrl,
                        coverPhotoUrl: updatedTemplate.about.coverPhotoUrl,
                        companyLogoUrl: updatedTemplate.about.companyLogoUrl
                    },
                    profileIsFirebase: updatedTemplate.about.profilePictureUrl && !updatedTemplate.about.profilePictureUrl.startsWith('blob:'),
                    coverIsFirebase: updatedTemplate.about.coverPhotoUrl && !updatedTemplate.about.coverPhotoUrl.startsWith('blob:'),
                    logoIsFirebase: updatedTemplate.about.companyLogoUrl && !updatedTemplate.about.companyLogoUrl.startsWith('blob:')
                });
            }

            setTemplate(updatedTemplate);
            // Update context with the complete updated template (ensures image URLs are in context)
            setTemplateSection(currentTemplateId, section, section === 'about' ? updatedTemplate.about : data);
            updateTemplateState(currentTemplateId, updatedTemplate);
        }
    };

    const handleSave = async (imageUrls = null) => {
        if (!teamId) {
            showSnackbar('Team ID is required', 'error');
            return;
        }

        // Get the latest template data from context (which is updated synchronously)
        // This ensures we have the latest image URLs and other data
        const contextTemplate = templateState[currentTemplateId] || template;

        // CRITICAL: If imageUrls are provided directly (from handleSaveWithImages),
        // use them instead of reading from state. This ensures we get the Firebase URLs
        // even if state hasn't updated yet (React state updates are async)
        console.log('[TeamTemplateEditContainer] handleSave called with imageUrls:', imageUrls);

        // CRITICAL: Helper function to get the best image URL (prefer Firebase URLs, reject blob URLs)
        // We need to check BOTH template prop AND context, and prefer the most recent Firebase URL
        const getImageUrl = (templateUrl, contextUrl) => {
            console.log('[TeamTemplateEditContainer] getImageUrl called:', {
                templateUrl,
                templateUrlType: typeof templateUrl,
                templateUrlIsBlob: templateUrl?.startsWith('blob:'),
                templateUrlIsFirebase: templateUrl && typeof templateUrl === 'string' && !templateUrl.startsWith('blob:') && templateUrl.startsWith('http'),
                contextUrl,
                contextUrlType: typeof contextUrl,
                contextUrlIsBlob: contextUrl?.startsWith('blob:'),
                contextUrlIsFirebase: contextUrl && typeof contextUrl === 'string' && !contextUrl.startsWith('blob:') && contextUrl.startsWith('http')
            });

            // CRITICAL: Prefer template URL (from onTemplateUpdate) - it's the most recent
            // Only use it if it's a valid Firebase URL (not blob, not null, not empty, starts with http)
            if (templateUrl &&
                typeof templateUrl === 'string' &&
                !templateUrl.startsWith('blob:') &&
                templateUrl.trim() &&
                templateUrl.startsWith('http')) {
                console.log('[TeamTemplateEditContainer] ✅ Using templateUrl (Firebase URL):', templateUrl.substring(0, 80) + '...');
                return templateUrl;
            }

            // Fall back to context URL if template URL is null/blob/empty/invalid
            // But only if context URL is also a valid Firebase URL (not blob)
            if (contextUrl &&
                typeof contextUrl === 'string' &&
                !contextUrl.startsWith('blob:') &&
                contextUrl.trim() &&
                contextUrl.startsWith('http')) {
                console.log('[TeamTemplateEditContainer] ✅ Using contextUrl (Firebase URL):', contextUrl.substring(0, 80) + '...');
                return contextUrl;
            }

            // If both are null/undefined/blob/empty/invalid, return null
            console.log('[TeamTemplateEditContainer] ⚠️ No valid Firebase URL found, returning null');
            return null;
        };

        // CRITICAL: Get image URLs - prioritize direct parameters (from handleSaveWithImages),
        // then template prop, then context
        // This ensures we get the Firebase URLs even if state hasn't updated yet
        const profilePictureUrl = imageUrls?.profilePictureUrl ||
            getImageUrl(template?.about?.profilePictureUrl, contextTemplate?.about?.profilePictureUrl);
        const coverPhotoUrl = imageUrls?.coverPhotoUrl ||
            getImageUrl(template?.about?.coverPhotoUrl, contextTemplate?.about?.coverPhotoUrl);
        const companyLogoUrl = imageUrls?.companyLogoUrl ||
            getImageUrl(template?.about?.companyLogoUrl, contextTemplate?.about?.companyLogoUrl);

        console.log('[TeamTemplateEditContainer] Final image URLs for save:', {
            fromImageUrlsParam: imageUrls,
            profilePictureUrl,
            coverPhotoUrl,
            companyLogoUrl,
            profileIsFirebase: profilePictureUrl && !profilePictureUrl.startsWith('blob:'),
            coverIsFirebase: coverPhotoUrl && !coverPhotoUrl.startsWith('blob:'),
            logoIsFirebase: companyLogoUrl && !companyLogoUrl.startsWith('blob:')
        });

        console.log('[TeamTemplateEditContainer] Image URLs before save:', {
            fromTemplate: {
                profile: template?.about?.profilePictureUrl,
                cover: template?.about?.coverPhotoUrl,
                logo: template?.about?.companyLogoUrl
            },
            fromContext: {
                profile: contextTemplate?.about?.profilePictureUrl,
                cover: contextTemplate?.about?.coverPhotoUrl,
                logo: contextTemplate?.about?.companyLogoUrl
            },
            final: {
                profile: profilePictureUrl,
                cover: coverPhotoUrl,
                logo: companyLogoUrl
            }
        });

        // Deep merge to ensure all nested properties are included, especially image URLs
        const templateToSave = {
            ...template,
            ...contextTemplate, // Merge context data (which has latest updates) with local state
            // Ensure about section is properly merged with all properties including image URLs
            about: {
                ...(template.about || {}),
                ...(contextTemplate.about || {}),
                // CRITICAL: Explicitly include image URLs with priority order
                // 1. template.about (from onTemplateUpdate - most recent)
                // 2. contextTemplate.about (from context)
                // 3. null (if neither has it)
                profilePictureUrl: profilePictureUrl,
                coverPhotoUrl: coverPhotoUrl,
                companyLogoUrl: companyLogoUrl
            }
        };

        // Log the about section to debug
        console.log('[TeamTemplateEditContainer] Template about section to save:', templateToSave.about);
        console.log('[TeamTemplateEditContainer] Image URLs in about:', {
            profilePictureUrl: templateToSave.about.profilePictureUrl,
            coverPhotoUrl: templateToSave.about.coverPhotoUrl,
            companyLogoUrl: templateToSave.about.companyLogoUrl,
            profilePictureUrlType: typeof templateToSave.about.profilePictureUrl,
            coverPhotoUrlType: typeof templateToSave.about.coverPhotoUrl,
            companyLogoUrlType: typeof templateToSave.about.companyLogoUrl,
            profileIsFirebase: templateToSave.about.profilePictureUrl && !templateToSave.about.profilePictureUrl.startsWith('blob:'),
            coverIsFirebase: templateToSave.about.coverPhotoUrl && !templateToSave.about.coverPhotoUrl.startsWith('blob:'),
            logoIsFirebase: templateToSave.about.companyLogoUrl && !templateToSave.about.companyLogoUrl.startsWith('blob:')
        });

        // CRITICAL: Verify image URLs are not blob URLs before saving
        if (templateToSave.about.profilePictureUrl && templateToSave.about.profilePictureUrl.startsWith('blob:')) {
            console.error('[TeamTemplateEditContainer] ⚠️ ERROR: profilePictureUrl is a blob URL, not a Firebase URL!', templateToSave.about.profilePictureUrl);
        }
        if (templateToSave.about.coverPhotoUrl && templateToSave.about.coverPhotoUrl.startsWith('blob:')) {
            console.error('[TeamTemplateEditContainer] ⚠️ ERROR: coverPhotoUrl is a blob URL, not a Firebase URL!', templateToSave.about.coverPhotoUrl);
        }
        if (templateToSave.about.companyLogoUrl && templateToSave.about.companyLogoUrl.startsWith('blob:')) {
            console.error('[TeamTemplateEditContainer] ⚠️ ERROR: companyLogoUrl is a blob URL, not a Firebase URL!', templateToSave.about.companyLogoUrl);
        }

        if (!templateToSave.templateName || templateToSave.templateName.trim() === '') {
            showSnackbar('Template name is required', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            console.log('[TeamTemplateEditContainer] Saving template:', {
                isCreating,
                templateId,
                templateName: templateToSave.templateName,
                about: templateToSave.about
            });

            // CRITICAL: Log the exact payload being sent to the backend
            console.log('[TeamTemplateEditContainer] 📤 Sending template to backend:', {
                isCreating,
                templateId,
                templateName: templateToSave.templateName,
                about: templateToSave.about,
                aboutProfileUrl: templateToSave.about?.profilePictureUrl,
                aboutCoverUrl: templateToSave.about?.coverPhotoUrl,
                aboutLogoUrl: templateToSave.about?.companyLogoUrl,
                aboutKeys: Object.keys(templateToSave.about || {}),
                fullPayload: JSON.stringify(templateToSave, null, 2)
            });

            let savedTemplate;
            if (isCreating) {
                // Create template first to get the template ID
                const response = await createTemplate(teamId, templateToSave);
                savedTemplate = response.template;

                console.log('[TeamTemplateEditContainer] ✅ Template created:', savedTemplate);
                console.log('[TeamTemplateEditContainer] Created template image URLs:', {
                    profilePictureUrl: savedTemplate?.about?.profilePictureUrl,
                    coverPhotoUrl: savedTemplate?.about?.coverPhotoUrl,
                    companyLogoUrl: savedTemplate?.about?.companyLogoUrl
                });
                showSnackbar('Template created successfully', 'success');

                // Update local template state with saved response (includes image URLs from database)
                setTemplate(savedTemplate);
                updateTemplateState(currentTemplateId, savedTemplate);

                // Navigate to templates list page after successful creation
                navigate('/team/templates');
            } else {
                // For existing templates, update with current data (including image URLs)
                console.log('[TeamTemplateEditContainer] 📤 Updating template with data:', templateToSave);
                const response = await updateTemplate(teamId, templateId, templateToSave);
                savedTemplate = response.template;

                console.log('[TeamTemplateEditContainer] ✅ Template updated:', savedTemplate);
                console.log('[TeamTemplateEditContainer] Updated template image URLs:', {
                    profilePictureUrl: savedTemplate?.about?.profilePictureUrl,
                    coverPhotoUrl: savedTemplate?.about?.coverPhotoUrl,
                    companyLogoUrl: savedTemplate?.about?.companyLogoUrl
                });
                showSnackbar('Template updated successfully', 'success');

                // Update local template state with saved response (includes image URLs from database)
                setTemplate(savedTemplate);
                updateTemplateState(currentTemplateId, savedTemplate);

                // Stay on the edit page after updating (user can continue editing)
            }
        } catch (error) {
            console.error('[TeamTemplateEditContainer] Error saving template:', error);
            console.error('[TeamTemplateEditContainer] Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            showSnackbar(error.message || 'Failed to save template', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/team/templates');
    };

    const handleAssignMembers = () => {
        if (!isCreating && template?._id) {
            setAssignModalOpen(true);
        }
    };

    const handleAssignMembersSave = async ({ templateId, assignMemberIds, unassignMemberIds }) => {
        if (!teamId || !templateId) {
            showSnackbar('Team ID and Template ID are required', 'error');
            return;
        }

        try {
            await assignMembersToTemplate(teamId, templateId, {
                assignMemberIds,
                unassignMemberIds
            });
            showSnackbar('Members assigned successfully', 'success');
            setAssignModalOpen(false);
            // Reload template to get updated assigned members
            const response = await getTemplate(teamId, templateId);
            if (response && response.success && response.template) {
                setTemplate(response.template);
                updateTemplateState(currentTemplateId, response.template);
            }
        } catch (error) {
            console.error('Error assigning members:', error);
            showSnackbar(error.message || 'Failed to assign members', 'error');
        }
    };

    const handleSetAsDefault = async () => {
        if (!isCreating && template?._id && teamId) {
            try {
                // Get current template's assigned members
                const assignedMembers = template.assignedMembers || [];

                if (assignedMembers.length === 0) {
                    showSnackbar('No cards are assigned to this template', 'info');
                    return;
                }

                // Find the default template for this team
                const templatesResponse = await getTemplates(teamId);
                const defaultTemplate = templatesResponse?.templates?.find(t => t.isDefault === true);

                if (!defaultTemplate) {
                    showSnackbar('No default template found for this team. Please set a default template first.', 'error');
                    return;
                }

                // If the current template is already the default, show message
                if (template._id === defaultTemplate._id) {
                    showSnackbar('This template is already the default template', 'info');
                    return;
                }

                // Confirm the action
                const confirmed = window.confirm(
                    `Are you sure you want to reassign all ${assignedMembers.length} member(s) from "${template.templateName}" to the default template "${defaultTemplate.templateName}"?`
                );

                if (!confirmed) {
                    return;
                }

                setIsSubmitting(true);

                // Extract member IDs - handle both ObjectId and user ID formats
                const memberIds = assignedMembers.map(member => {
                    // If member is an object with _id or userId, extract it
                    if (typeof member === 'object') {
                        return member._id || member.userId || member;
                    }
                    // If it's already a string/ID, use it directly
                    return member;
                });

                // Unassign from current template
                await assignMembersToTemplate(teamId, template._id, {
                    assignMemberIds: [],
                    unassignMemberIds: memberIds,
                    setAsDefault: false
                });

                // Assign to default template
                await assignMembersToTemplate(teamId, defaultTemplate._id, {
                    assignMemberIds: memberIds,
                    unassignMemberIds: [],
                    setAsDefault: false
                });

                showSnackbar(
                    `Successfully reassigned ${memberIds.length} member(s) to the default template`,
                    'success'
                );

                // Reload the current template to reflect the changes
                if (templateId) {
                    const response = await getTemplate(teamId, templateId);
                    if (response && response.success && response.template) {
                        setTemplate(response.template);
                        updateTemplateState(templateId, response.template);
                    }
                }
            } catch (error) {
                console.error('Error setting assigned cards to default:', error);
                showSnackbar(error.message || 'Failed to reassign members to default template', 'error');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleDuplicate = async () => {
        if (!isCreating && template?._id && teamId) {
            try {
                setIsSubmitting(true);

                // Create a copy of the template with "Copy" appended to the name
                const duplicateTemplateData = {
                    templateName: `${template.templateName} (Copy)`,
                    about: template.about || {},
                    links: template.links || {},
                    qrCode: template.qrCode || {},
                    leadCapture: template.leadCapture || {},
                    followUpEmail: template.followUpEmail || {},
                    settings: template.settings || {},
                    isDefault: false // Don't set duplicate as default
                };

                const response = await createTemplate(teamId, duplicateTemplateData);

                if (response && response.success) {
                    showSnackbar('Template duplicated successfully', 'success');
                    // Navigate to the new template after a short delay
                    setTimeout(() => {
                        navigate(`/team/templates/edit/${response.template._id}`);
                    }, 1000);
                } else {
                    showSnackbar('Failed to duplicate template', 'error');
                }
            } catch (error) {
                console.error('Error duplicating template:', error);
                showSnackbar(error.message || 'Failed to duplicate template', 'error');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleRemove = async () => {
        if (!isCreating && template?._id && teamId) {
            // Confirm deletion
            const confirmed = window.confirm(
                `Are you sure you want to remove "${template.templateName}"? This action cannot be undone.`
            );

            if (!confirmed) {
                return;
            }

            try {
                setIsSubmitting(true);
                await deleteTemplate(teamId, template._id);
                showSnackbar('Template removed successfully', 'success');
                // Navigate back to templates list after a short delay
                setTimeout(() => {
                    navigate('/team/templates');
                }, 1000);
            } catch (error) {
                console.error('Error removing template:', error);
                showSnackbar(error.message || 'Failed to remove template', 'error');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Box>Loading template...</Box>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexShrink: 0 }}>
                <TeamTemplateEditNavbar
                    templateName={template.templateName}
                    isEditing={!isCreating}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    isSubmitting={isSubmitting}
                    nextTemplateNumber={isCreating ? nextTemplateNumber : null}
                    templateId={template?._id}
                    onAssignMembers={handleAssignMembers}
                    onSetAsDefault={handleSetAsDefault}
                    onDuplicate={handleDuplicate}
                    onRemove={handleRemove}
                />
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <TeamTemplateEditLayout
                    onMenuItemClick={setSelectedItem}
                    selectedItem={selectedItem}
                    mainContent={
                        <TeamTemplateEditContent
                            selectedItem={selectedItem}
                            template={template}
                            onTemplateUpdate={handleTemplateUpdate}
                            isCreating={isCreating}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            isSubmitting={isSubmitting}
                            user={user}
                            setSelectedItem={setSelectedItem}
                        />
                    }
                    previewContent={
                        <TeamTemplatePreview
                            selectedItem={selectedItem}
                            template={template}
                        />
                    }
                />
            </Box>

            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleCloseSnackbar}
            />

            {!isCreating && template?._id && (
                <TeamTemplateAssignMembersModal
                    open={assignModalOpen}
                    onClose={() => setAssignModalOpen(false)}
                    template={template}
                    teamId={teamId}
                    onSave={handleAssignMembersSave}
                />
            )}
        </Box>
    );
};

const TeamTemplateEditContainer = ({ user, team }) => {
    return (
        <TeamTemplateProvider>
            <TeamTemplateEditContainerInner user={user} team={team} />
        </TeamTemplateProvider>
    );
};

export default TeamTemplateEditContainer;




